import * as React from "react";
import { useState } from "react";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  Table,
  Box,
  Header,
  SpaceBetween,
  Grid,
  Button,
  Pagination,
  TextFilter,
  CollectionPreferences,
} from "@amzn/awsui-components-react";
import PropTypes from "prop-types";
import { RecommendationItem } from "../../calcBusterTypes";
import { USDollar } from "../../util";
import {
  EmptyState,
  COLUMN_DEFINITIONS,
  getMatchesCountText,
  collectionPreferencesProps,
  convertToColumnProps,
} from "./util";

interface PreferencesType {
  pageSize: number;
  visibleContent: string[];
}

const ids = [
  "id",
  "severity",
  "service",
  "recommendation",
  "savings",
  "tags",
  "instances",
];

function sumBySavings(selectedItems: readonly RecommendationItem[]) {
  const savingsValues = selectedItems.map((item) => item.savings);
  const sum = savingsValues.reduce((total, value) => total + value, 0);
  return sum;
}

const SummaryFooter = (
  selectedItems: readonly RecommendationItem[],
  annualRunRate: number
) => {
  const selectedSavingsSum = sumBySavings(selectedItems);
  return (
    <Grid
      gridDefinition={[
        { colspan: 6 },
        { colspan: 6 },
        { colspan: 6 },
        { colspan: 6 },
      ]}
    >
      <Box>
        <b>Total</b>
      </Box>
      <Box
        float="right"
        variant="p"
        margin={{ right: "xxl" }}
        padding={{ right: "xxl" }}
      >
        {USDollar.format(selectedSavingsSum)}
      </Box>
      <Box>
        <b>Annual Run Rate</b>
      </Box>
      <Box
        float="right"
        variant="p"
        margin={{ right: "xxl" }}
        padding={{ right: "xxl" }}
      >
        {USDollar.format(annualRunRate - selectedSavingsSum)}
      </Box>
    </Grid>
  );
};

const RecommendationsTable: React.FC<{
  recommendations: RecommendationItem[];
  annualRunRate: number;
}> = ({ recommendations, annualRunRate }) => {
  const DEFAULT_PREFERENCES: PreferencesType = {
    pageSize: 10,
    visibleContent: [...ids],
  };
  const [preferences, setPreferences] =
    useState<PreferencesType>(DEFAULT_PREFERENCES);

  const {
    items,
    actions,
    filteredItemsCount = 0,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(recommendations, {
    filtering: {
      empty: (
        <EmptyState
          title="No Recommendations"
          subtitle="There are currently no cost optimization recommendations for this analysis."
        />
      ),
      noMatch: (
        <EmptyState
          title="No matches"
          action={
            <Button onClick={() => actions.setFiltering("")}>
              Clear filter
            </Button>
          }
        />
      ),
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {
      defaultState: {
        sortingColumn: {
          sortingField: "savings",
        },
        isDescending: true,
      },
    },
    selection: {},
  });

  const { selectedItems = [] } = collectionProps || {};

  const paginationLabels = {
    nextPageLabel: "Next page",
    pageLabel: (pageNumber: number) => `Go to page ${pageNumber}`,
    previousPageLabel: "Previous page",
  };

  return (
    <SpaceBetween size="l">
      <Table
        {...collectionProps}
        selectionType={"multi"}
        header={
          <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
            <Header
              counter={
                selectedItems.length
                  ? `(${selectedItems.length}/${recommendations.length})`
                  : `(${recommendations.length})`
              }
            >
              Recommendations
            </Header>
          </Grid>
        }
        // trackBy='id'
        variant={"embedded"}
        stickyColumns={{ first: 1, last: 0 }}
        stickyHeader
        wrapLines
        columnDefinitions={COLUMN_DEFINITIONS}
        columnDisplay={convertToColumnProps(preferences.visibleContent, ids)}
        items={items}
        pagination={
          <Pagination {...paginationProps} ariaLabels={paginationLabels} />
        }
        filter={
          <TextFilter
            {...filterProps}
            countText={getMatchesCountText(filteredItemsCount)}
            filteringAriaLabel="Filter recommendations"
          />
        }
        preferences={
          <CollectionPreferences
            {...collectionPreferencesProps}
            preferences={preferences}
            onConfirm={({ detail }) => {
              const updatedPreferences: PreferencesType = {
                pageSize: detail.pageSize ?? preferences.pageSize,
                visibleContent: detail.visibleContent
                  ? [...detail.visibleContent]
                  : preferences.visibleContent,
              };
              setPreferences(updatedPreferences);
            }}
          />
        }
        footer={
          selectedItems.length === 0 ? (
            <></>
          ) : (
            SummaryFooter(selectedItems, annualRunRate)
          )
        }
      />
    </SpaceBetween>
  );
};

RecommendationsTable.propTypes = {
  recommendations: PropTypes.array.isRequired,
  annualRunRate: PropTypes.number.isRequired,
};

export default RecommendationsTable;
